/**
 * ========================
 * GLOBAL MENU RENDERER
 * ========================
 *
 * Renders global menu sections (App, User, Admin).
 *
 * Similar to MenuRenderer but queries global provider instead of domain provider.
 */

import { useMemo, cloneElement, isValidElement } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { NavLink, Stack, Text, Badge, rem, Tooltip } from '@mantine/core';
import { getGlobalMenuProvider } from '../../../domain/DomainMenuRegistry';
import { MenuItem, MenuSection } from '../types';
import { AccessControl, hasAccess } from '../../../components/security/AccessControl';
import { usePreferencesStore } from '../../../stores/preferencesStore';
import { useUserContext } from '../../../domain/providers/BaseMenuProvider';
import { useAuth } from '@actschurch/ccms-sdk';

interface GlobalMenuRendererProps {
  className?: string;
}

/**
 * Pure menu renderer for global navigation.
 *
 * Queries the global menu provider for menu configuration,
 * applies user preferences, and renders menu items with
 * permission checks.
 */
export function GlobalMenuRenderer({ className }: GlobalMenuRendererProps) {
  const location = useLocation();

  // Get menu preferences from store
  const menuIconOnly = usePreferencesStore((state) => state.menuIconOnly);
  const menuDensity = usePreferencesStore((state) => state.menuDensity);
  const menuShowSectionCounts = usePreferencesStore((state) => state.menuShowSectionCounts);

  // Query registry for global provider
  const provider = useMemo(() => getGlobalMenuProvider('global'), []);
  const menuConfig = useMemo(() => provider?.getMenuConfig() ?? null, [provider]);

  // Get user context for visibility checks
  const userContext = useUserContext();

  // Get auth state for access checks
  const { state: authState } = useAuth();
  const userRoles = authState.user?.roles ?? [];
  const userPermissions = authState.user?.rights ?? [];
  const userDomains = (authState.user as any)?.domains || [];

  // Active route detection
  const isActive = (path: string) => location.pathname === path;

  // Get density-based padding
  const getDensityPadding = () => {
    switch (menuDensity) {
      case 'compact':
        return rem(6);
      case 'comfortable':
        return rem(12);
      default: // 'regular'
        return rem(8);
    }
  };

  /**
   * Render a single menu item.
   * Applies visibility check, access control, and preference-based styling.
   */
  const renderMenuItem = (item: MenuItem) => {
    // Check visibility function
    const isVisible = item.visible ? item.visible(userContext) : true;
    if (!isVisible) return null;

    // Evaluate count selector if present
    const count = item.countSelector && menuShowSectionCounts ? item.countSelector() : null;

    const navLink = (
      <NavLink
        component={Link}
        to={item.path}
        label={item.label}
        leftSection={
          menuIconOnly ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: rem(40),
              }}
              role="img"
              aria-label={item.label}
            >
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {item.icon && isValidElement(item.icon)
                  ? cloneElement(item.icon as React.ReactElement<any>, {
                      size: 22,
                      'aria-hidden': 'true'
                    })
                  : item.icon}
                {count && (
                  <Badge
                    size="sm"
                    variant="filled"
                    color={item.badgeColor || 'blue'}
                    radius="xl"
                    aria-label={`${count} items`}
                    style={{
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      minWidth: 18,
                      height: 18,
                      padding: '0 4px',
                      fontSize: '10px',
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {count}
                  </Badge>
                )}
              </div>
            </div>
          ) : (
            item.icon
          )
        }
        rightSection={
          !menuIconOnly && count ? (
            <Badge size="xs" variant="filled" color={item.badgeColor || 'blue'} radius="sm">
              {count}
            </Badge>
          ) : null
        }
        active={isActive(item.path)}
        styles={{
          root: {
            padding: menuIconOnly
              ? `${rem(6)} 0`
              : `${getDensityPadding()} ${rem(8)}`,
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            maxWidth: '100%',
            overflow: 'visible',
            boxSizing: 'border-box',
            position: 'relative',
            margin: 0,
            display: 'flex',
            minHeight: menuIconOnly ? rem(48) : 'auto',
          },
          label: {
            display: menuIconOnly ? 'none' : 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
            minWidth: 0,
            maxWidth: '100%',
          },
          body: {
            display: menuIconOnly ? 'none' : 'block',
          },
          section: {
            flexShrink: 0,
            maxWidth: '100%',
            overflow: menuIconOnly ? 'visible' : 'hidden',
          },
        }}
      />
    );

    return (
      <AccessControl
        key={item.id}
        roles={item.requiredRoles}
        permissions={item.requiredPermissions}
        requireAll={item.requireAll}
        strictAccess={item.strictAccess}
      >
        {menuIconOnly ? (
          <Tooltip label={item.label} position="right" withArrow>
            {navLink}
          </Tooltip>
        ) : (
          navLink
        )}
      </AccessControl>
    );
  };

  /**
   * Check if a menu item is accessible to the current user.
   */
  const isItemAccessible = (item: MenuItem): boolean => {
    // Check visibility function first
    const isVisible = item.visible ? item.visible(userContext) : true;
    if (!isVisible) return false;

    // Check access control (roles/permissions)
    return hasAccess(userRoles, userPermissions, userDomains, {
      roles: item.requiredRoles,
      permissions: item.requiredPermissions,
      requireAll: item.requireAll,
      strictAccess: item.strictAccess,
    });
  };

  /**
   * Render a menu section with header and items.
   * Sections are non-collapsible headers as per user requirements.
   */
  const renderSection = (section: MenuSection, index: number) => {
    // Check section visibility
    const isVisible = section.visible ? section.visible(userContext) : true;
    if (!isVisible) return null;

    // Count visible items to determine if we should show empty state
    const visibleItemsCount = section.items.filter(isItemAccessible).length;

    // Hide Admin section entirely if user has no admin access
    if (section.label.toLowerCase() === 'admin' && visibleItemsCount === 0) {
      return null;
    }

    // Evaluate section count selector if present
    const sectionCount =
      menuShowSectionCounts && section.countSelector ? section.countSelector() : null;

    // Get empty state message based on section label (only for App section now)
    const getEmptyMessage = () => {
      switch (section.label.toLowerCase()) {
        case 'app':
          return 'No apps assigned to you';
        default:
          return null;
      }
    };

    const emptyMessage = getEmptyMessage();

    return (
      <Stack
        key={section.id}
        gap={menuIconOnly ? 0 : "xs"}
        mt={index > 0 ? (menuIconOnly ? 0 : 'md') : 0}
        align="flex-start"
        style={{
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden',
          boxSizing: 'border-box',
        }}
      >
        {/* Section header - only show if not in icon-only mode */}
        {!menuIconOnly && (
          <Text
            size="10px"
            c="gray.6"
            fw={500}
            tt="uppercase"
            ta="left"
            opacity={0.7}
            style={{
              width: '100%',
              maxWidth: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              overflow: 'hidden',
              boxSizing: 'border-box',
              paddingRight: rem(4),
            }}
          >
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
                minWidth: 0,
              }}
            >
              {section.label}
            </span>
            {sectionCount !== null && (
              <Badge size="xs" variant="light" color="gray" style={{ flexShrink: 0 }}>
                {sectionCount}
              </Badge>
            )}
          </Text>
        )}

        {/* Section items or empty state */}
        {visibleItemsCount > 0 ? (
          <Stack gap={menuIconOnly ? 0 : "xs"} style={{ width: '100%', maxWidth: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
            {section.items.map(renderMenuItem)}
          </Stack>
        ) : (
          !menuIconOnly && emptyMessage && (
            <Text size="xs" c="dimmed" fs="italic" py="xs" px="sm">
              {emptyMessage}
            </Text>
          )
        )}
      </Stack>
    );
  };

  // If provider not found or no config, show fallback
  if (!provider || !menuConfig) {
    return (
      <Stack p="md" gap="xs">
        <Text size="sm" c="dimmed">
          Global menu not available
        </Text>
      </Stack>
    );
  }

  return (
    <div
      style={{
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      <Stack
        gap="xs"
        p={menuIconOnly ? 0 : rem(12)}
        px={menuIconOnly ? rem(4) : rem(12)}
        className={className}
        style={{
          width: '100%',
          overflow: 'hidden',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        {/* Render all sections */}
        {menuConfig.sections.map(renderSection)}
      </Stack>
    </div>
  );
}
